import {_, React, observer, computed, useAsObservableSource, computedFn, IDisposer, autorun} from '@api'
import {AddNewHeroCardItem, AssortmentHeroCardStore, BuilderCardStore, HIDDEN_HEROES_KEY, ICatalogItemDetailsPathElement, IContextMenuItems, IContextualHoverItemStore, PlaceholderHeroCardImage, useAssortmentHeroCardStore, useVipLocalStore, VipLocalStore} from '@modules'
import {MyImage} from 'centric-ui-components'
import {action, observable, toJS} from 'mobx'
import {StoresInstance} from '@stores'
import {NoImagePlaceholder} from '@modules'
import {useEffect} from 'react'
import * as css from './AssortmentHeroCardItem.css'
import {Tooltip} from '@material-ui/core'

/**
 * Some specific item with potentially multiple images that exists at the final intersection of the assortment/hero/swatch combination
 * For RH - a colorway
 */
export class HeroCardItemStore extends VipLocalStore implements IContextMenuItems, IContextualHoverItemStore {
  constructor(stores: StoresInstance, public props: { hero: AssortmentHeroCardStore, item: any, index: number }) {
    super(stores)
  }

  registerEffects = () => {
    const _toDispose: Array<IDisposer> = []

    this.hero.heroCardItemStores[this.props.index] = this
    return () => {
      _toDispose.forEach(d => d())
      delete this.hero.heroCardItemStores[this.props.index]
    }
  }

  @computed get contextMenuItems() {
    return this.props.hero.contextMenuItems
  }

  get item() { return this.props.item}

  get parent() { return this.item?.__parent }

  get hero() { return this.props.hero}

  get imageConfig() {
    const {hero: {assortment: {config: {heroCards}}}} = this
    return heroCards?.child?.images
  }

  @computed get placeholderImage() {
    const {parent, item, imageConfig, catalog} = this,
      r = _.get(parent ?? item, _.isString(imageConfig) ? imageConfig : (imageConfig?.field ?? 'images'))

    return _.isString(r) ? r : _.isArray(r) ? _.first(r) : r
  }

  @computed get placeHolderUrl() {
    const {placeholderImage, catalog} = this
    return catalog.imageUrlFor(placeholderImage, {c8Size: 'largeImage', imageType: 'png'})
  }

  @computed get parentImageField() {
    const {imageConfig, hero: {dataMap}, es, item} = this

    return _.isString(imageConfig) ? imageConfig : (imageConfig?.field ?? 'images')
  }

  @computed get images() {
    const {hero: {imagesForHeroItem}, item} = this

    return imagesForHeroItem(item) ?? []
  }

  @computed get imageUrls() {
    const {catalog: {imageUrlFor, isShowingClientPDF}, hero: {dataMap}, images} = this
    return images.map(image => imageUrlFor(image, {source: _.isString(dataMap.images) ? null : dataMap.images.source, c8Size: 'largeImage', imageType: 'png'}))
  }

  @observable _selectedImageIndex = 0
  @computed get selectedImageIndex() {
    const {imageUrls, _selectedImageIndex} = this
    return Math.min(imageUrls.length - 1, _selectedImageIndex)
  }

  set selectedImageIndex(v) { this._selectedImageIndex = v}

  @computed get hasMoreImages() {
    const {imageUrls, _selectedImageIndex} = this

    return _selectedImageIndex < imageUrls.length - 1
  }

  @computed get tooltipTitle() {
    const {hero: {associatedHeroItemWorkspace}, es, item, strings} = this
    if (!associatedHeroItemWorkspace || !item) {
      return ''
    }

    const {dataMap, config: {itemName}} = associatedHeroItemWorkspace
    const v = es.readField(item, dataMap.title).valAsString ?? strings.emptyLabel
    return `${v}` //${itemName ? `\n(${itemName})` : ''}`
  }

  get field() { return this.hero.associatedHeroItemField}

  @computed get detailsPath(): ICatalogItemDetailsPathElement[] {
    const {
      catalog, id, associatedWorkspace, field, item
    } = this

    return [
      field && item?.__parent ? {
        where: [{value: catalog.idFor(item.__parent)}],
        esIndex: catalog.index
      } : undefined,
      {
        where: [{value: id}],
        esIndex: associatedWorkspace?.config?.catalog?.index ?? catalog?.index,
        catalogField: field?.field
      }
    ].filter(_.identity)
  }

  @computed get id() {
    const {item, catalog: {idFor}} = this
    return idFor(item, false)
  }

  get associatedWorkspace() {
    return this.hero.associatedHeroItemWorkspace
  }

  @computed get mouseEvents() {
    return this.hero.cardStore.card.hoverItemEventsFor(this)
  }

  get assortment() { return this.props.hero.assortment}

  @action onDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation()

    const {
      stores: {ui, flexLayoutCatalogRoot: {tab}},
      props: {hero: {assortment}}, detailsPath, associatedWorkspace
    } = this

    !_.isEmpty(detailsPath) && associatedWorkspace && tab.openCatalogItemDetailsInNewTab({
      path: detailsPath,
      selectedPathIndex: 0,
      workspace: associatedWorkspace._id,
      parentTab: tab.id
    })

    setTimeout(() => assortment.scrollIntoView(), ui.settings.flexLayout.scrollIntoViewDelayAfterNewChildTab)
  }

  @computed get hoverId() {
    return this.id
  }

  matchesHoverItem = computedFn((card: BuilderCardStore): boolean => {
    const {id, props: {nested}} = card,
      {hero: {selectedHeroItemRootId}} = this

    let r = (selectedHeroItemRootId === id)

    return r
  })

  @computed get isSelected() {
    const {props: {index, item}, hero: {selectedHeroItem, selectedHeroIndex}} = this
    return _.isNumber(selectedHeroIndex) ? selectedHeroIndex === index : selectedHeroItem === item
  }
}

export const HeroCardItemStoreContext = React.createContext<HeroCardItemStore>(null)
export const useHeroCardItemStore = () => React.useContext(HeroCardItemStoreContext)

/**
 * Some specific item with potentially multiple images that exists at the final intersection of the assortment/hero/swatch combination
 * For RH - a colorway
 */
export const AssortmentHeroCardItem = observer<{ heroKey: string, item: any, index: number }>(function AssortmentHeroCardItem({heroKey, item, index}) {
  const hero = useAssortmentHeroCardStore(),
    obsProps = useAsObservableSource({heroKey, item, index, hero}),
    store = useVipLocalStore((ws) => new HeroCardItemStore(ws, obsProps)),
    {onDoubleClick, associatedWorkspace, catalog, imageUrls, selectedImageIndex, tooltipTitle, mouseEvents, isSelected, images, assortment: {assortmentCardSettings: {}}} = store,
    {selectedHeroIndex, selectedHeroItem} = hero,
    {catalog: {features}} = associatedWorkspace,
    cardFeatures = features?.card

  useEffect(() => store.registerEffects(), [])

  return (
    <HeroCardItemStoreContext.Provider value={store}>
      {/*<Tooltip placement='bottom' title={tooltipTitle}>*/}
      <div className={css.root}
           data-images={imageUrls.length}
           data-index={index}
           data-selected={isSelected}
           {...mouseEvents}
           style={{
             '--hero-card-inner-image-padding': cardFeatures?.heroImagePadding ?? cardFeatures?.imagePadding,
             '--card-image-align-self': cardFeatures?.alignSelf
           } as any}
           onDoubleClick={onDoubleClick}
           data-id={catalog.idFor(item)}>
        {_.isEmpty(imageUrls)
         ? <PlaceholderHeroCardItemImage/>
         : _.take([imageUrls[selectedImageIndex]], 1).map((url, i) => (
            <MyImage
              key={i}
              data-index={i}
              className={css.heroCardImage}
              imageUrl={url}
              lazy={false}
              noImageContent={<PlaceholderHeroCardImage/>}
              imageId={catalog.idFor(images[selectedImageIndex], false)}

              // onLoad={onImageLoad}
            />))}
      </div>
      {/*</Tooltip>*/}
    </HeroCardItemStoreContext.Provider>)
})

export const PlaceholderHeroCardItemImage = observer(function PlaceholderHeroCardItemImage() {
  const {item, parent, catalog, imageConfig, placeholderImage, placeHolderUrl} = useHeroCardItemStore()

  if (!item) return <AddNewHeroCardItem/>

  const imageField = _.isString(imageConfig) ? imageConfig : (imageConfig?.field ?? 'images'),
    images = _.get(parent ?? item, imageField),
    catalogField = catalog.findFieldInTree(imageField, true),
    associatedWorkspace = catalog.associatedWorkspaceFor(catalogField),
    cardFeatures = associatedWorkspace?.catalog?.features?.card

  return (
    <div className={css.noImageHeroItemPlaceholder}
         style={{
           '--hero-card-inner-image-padding': cardFeatures?.heroImagePadding ?? cardFeatures?.imagePadding,
           '--card-image-align-self': cardFeatures?.alignSelf
         } as any}>
      {_.isEmpty(images) ? <NoImagePlaceholder/>
                         : <MyImage className={css.placeholderImage}
                                    imageId={_.isString(placeholderImage) ? placeholderImage : placeholderImage?.exId}
                                    imageUrl={placeHolderUrl}/>}
    </div>)
})


// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/Assortments/AssortmentCard/HeroCards/AssortmentHeroCardItem.tsx